import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Upload, Shield, LogOut, Plus, Search, Grid, List, ArrowLeft, Eye, Download, Trash2, FolderPlus, Folder, File, Key, Loader2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useVault } from '../contexts/VaultContext'
import FileGrid from '../components/FileGrid'
import UploadSection from '../components/UploadSection'
import FileViewer from '../components/FileViewer'
import PasswordModal from '../components/PasswordModal'
import PasswordManager from '../components/PasswordManager'

const VaultPage = () => {
  const { user, signOut } = useAuth()
  const { files, loading, currentPath, folderView, listFiles, goBack, downloadFile } = useVault()
  const [currentView, setCurrentView] = useState('files') // 'files' or 'passwords'
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [showFileViewer, setShowFileViewer] = useState(false)
  const [viewerData, setViewerData] = useState(null)

  useEffect(() => {
    if (user) {
      listFiles()
    }
  }, [listFiles, user])


  const handleItemClick = async (item) => {
    setSelectedItem(item)
    
    // If we're inside a folder and clicking on a file, try to use cached password
    if (!item.isFolder && folderView && folderView.password) {
      try {
        // Try to download with cached password (pass null as password to use cached)
        const decryptedData = await downloadFile(item.path, null, item.displayName)
        
        // Show file viewer directly
        handleFileView({
          data: decryptedData,
          fileName: item.displayName,
          mimeType: getMimeType(item.displayName)
        })
        
        return // Don't show password modal
      } catch (error) {
        // Fall through to show password modal if cached password fails
      }
    }
    
    // Show password modal for folders or when cached password fails
    setShowPasswordModal(true)
  }

  const handlePasswordSubmit = (password) => {
    // This will be handled by the PasswordModal component
    setShowPasswordModal(false)
  }

  const handleFileView = (data) => {
    setViewerData(data)
    setShowFileViewer(true)
  }

  const getUserInitial = () => {
    return user?.email?.charAt(0).toUpperCase() || 'U'
  }

  const getMimeType = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    const mimeTypes = {
      // Images
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      // Videos
      mp4: 'video/mp4',
      webm: 'video/webm',
      avi: 'video/avi',
      mov: 'video/quicktime',
      // Documents
      pdf: 'application/pdf',
      txt: 'text/plain',
      md: 'text/markdown',
      json: 'application/json',
      csv: 'text/csv',
      // Default
      default: 'application/octet-stream'
    }
    
    return mimeTypes[extension] || mimeTypes.default
  }

  return (
    <div className="min-h-screen flex surface-primary">
      {/* Sidebar */}
      <motion.aside 
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="w-20 sm:w-64 sidebar p-3 sm:p-6 flex flex-col"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10">
          <motion.div
            animate={{ rotate: [0, 2, -2, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="p-2 bg-gray-100 rounded-lg"
          >
            <Shield className="w-8 h-8 text-gray-700" />
          </motion.div>
          <span className="text-2xl font-bold text-gray-900 hidden sm:inline">
            Stash
          </span>
        </div>

        {/* Navigation */}
        <nav className="space-y-2 flex-1">
          <button
            onClick={() => setCurrentView('files')}
            className={`w-full flex items-center justify-center sm:justify-start gap-3 px-2 sm:px-4 py-3 rounded-lg font-medium transition-colors ${
              currentView === 'files' 
                ? 'bg-gray-100 text-gray-700' 
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-700'
            }`}
            title="My Files"
          >
            <Upload className="w-5 h-5" />
            <span className="hidden sm:inline">My Files</span>
          </button>
          
          <button
            onClick={() => setCurrentView('passwords')}
            className={`w-full flex items-center justify-center sm:justify-start gap-3 px-2 sm:px-4 py-3 rounded-lg font-medium transition-colors ${
              currentView === 'passwords' 
                ? 'bg-gray-100 text-gray-700' 
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-700'
            }`}
            title="Password Manager"
          >
            <Key className="w-5 h-5" />
            <span className="hidden sm:inline">Password Manager</span>
          </button>
        </nav>

        {/* User Info */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-center sm:justify-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-bold border border-gray-200">
              {getUserInitial()}
            </div>
            <div className="flex-1 min-w-0 hidden sm:block">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.user_metadata?.full_name || user?.email}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email}
              </p>
            </div>
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 hover:border-gray-300"
            title="Sign Out"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main className="flex-1 p-6 lg:p-10 overflow-y-auto relative"
            style={{
              backgroundImage: 'url(/seashells-bg.svg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}>
        {/* Overlay for better readability */}
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm"></div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto relative z-10"
        >
          {currentView === 'files' ? (
            <>
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              {folderView && (
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  onClick={goBack}
                  className="text-gray-500 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeft className="w-6 h-6" />
                </motion.button>
              )}
              
              <div>
                <h1 className="text-4xl font-bold text-gray-900">
                  {folderView ? folderView.name : 'My Files'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {folderView 
                    ? `${files.length} files in folder`
                    : 'Your secure encrypted vault'
                  }
                </p>
              </div>
            </div>

            {/* Mobile user menu */}
            <div className="sm:hidden">
              <button
                onClick={signOut}
                className="text-gray-400 hover:text-red-500 transition-colors p-2"
              >
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Upload Section - only show in root directory */}
          {!folderView && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <UploadSection />
            </motion.div>
          )}

          {/* Files Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8"
          >
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">Loading your files...</p>
                </div>
              </div>
            ) : files.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center">
                  <Shield className="w-12 h-12 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Your vault is empty
                </h3>
                <p className="text-gray-600 mb-6">
                  Upload your first file to get started with secure storage
                </p>
              </motion.div>
            ) : (
              <FileGrid 
                files={files} 
                onItemClick={handleItemClick}
                isFolder={!!folderView}
              />
            )}
          </motion.div>
            </>
          ) : (
            <PasswordManager />
          )}
        </motion.div>
      </main>

      {/* Modals */}
      {showPasswordModal && (
        <PasswordModal
          item={selectedItem}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={handleFileView}
        />
      )}

      {showFileViewer && viewerData && (
        <FileViewer
          data={viewerData}
          onClose={() => {
            setShowFileViewer(false)
            setViewerData(null)
          }}
        />
      )}
    </div>
  )
}

export default VaultPage
